import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  OnInit,
  output,
} from '@angular/core';
import { NgOptimizedImage } from '@angular/common';
import { Observable, of } from 'rxjs';
import { Guid, isNotNil } from '@web/utils';
import {
  Icon,
  ImageThumbnailComponent,
  ImageThumbnailUnsupportedComponent,
  NoDataComponent,
  TableBodyNoReferenceCellComponent,
  TableBuilderFactory,
  TableComponent,
  TableConfig,
  TableDataPayload,
  TableService,
} from '@web/shared/ui';
import { AdminBoxSaleGroupCosmetic } from '../../models/admin-box-sale-group-cosmetic';

enum AdminBoxSaleGroupCosmeticsTableItemAction {
  Edit,
  Remove,
}

@Component({
  selector: 'app-admin-box-sale-group-cosmetics-table',
  imports: [
    ImageThumbnailComponent,
    ImageThumbnailUnsupportedComponent,
    NgOptimizedImage,
    NoDataComponent,
    TableBodyNoReferenceCellComponent,
    TableComponent,
  ],
  templateUrl: './admin-box-sale-group-cosmetics-table.component.html',
  styleUrl: './admin-box-sale-group-cosmetics-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [TableService],
})
export class AdminBoxSaleGroupCosmeticsTableComponent implements OnInit {
  /* prettier-ignore */
  private readonly builder = inject(TableBuilderFactory<AdminBoxSaleGroupCosmetic, never, AdminBoxSaleGroupCosmeticsTableItemAction>).create();
  private readonly table = inject(TableService);

  public readonly items = input.required<AdminBoxSaleGroupCosmetic[]>();
  public readonly allowChangeCosmetics = input.required<boolean>();
  public readonly showAvailableQuantity = input(true);
  public readonly showRemainingQuantity = input(true);
  public readonly editClickEvent = output<AdminBoxSaleGroupCosmetic>();
  public readonly removeClickEvent = output<Guid>();

  protected config!: TableConfig<
    AdminBoxSaleGroupCosmetic,
    never,
    AdminBoxSaleGroupCosmeticsTableItemAction
  >;

  protected readonly isNotNil = isNotNil;
  /* prettier-ignore */
  protected readonly getColumn = <Key extends keyof AdminBoxSaleGroupCosmetic>(key: Key): Key => key;

  public constructor() {
    this.initializeItemsRefreshEffect();
  }

  public ngOnInit(): void {
    this.buildTable();
  }

  private buildTable(): void {
    const builder = this.builder
      .dataKey('id')
      .pagination(false)
      .filtering(false)
      .columns((builder) => {
        const columns = [
          builder
            .initCustom('featureFileUrl')
            .width('6rem')
            .label('Zdjęcie ')
            .build(),

          builder.initCustom('name').width('15rem').label('Nazwa').build(),

          builder
            .initCustom('cosmeticBrandName')
            .width('12rem')
            .label('Marka')
            .build(),

          builder
            .initCustom('amount')
            .width('8rem')
            .label('Ilość w pudełku')
            .build(),
        ];

        if (this.showAvailableQuantity()) {
          columns.push(
            builder
              .initCustom('availableQuantity')
              .width('14rem')
              .label('Dostępna ilość do kupienia')
              .build(),
          );
        }

        if (this.showRemainingQuantity()) {
          columns.push(
            builder
              .initNumber('remainingQuantity')
              .width('14rem')
              .label('Pozostała ilość do kupienia')
              .build(),
          );
        }

        return columns;
      });

    builder.itemActions((builder) => [
      builder
        .initCommand(AdminBoxSaleGroupCosmeticsTableItemAction.Edit)
        .icon(Icon.Pencil)
        .tooltip('Edytuj ilości')
        .command((item) => this.editClickEvent.emit(item))
        .build(),
      builder
        .initCommand(AdminBoxSaleGroupCosmeticsTableItemAction.Remove)
        .icon(Icon.Trash)
        .tooltip('Usuń')
        .visibility((item) => this.allowChangeCosmetics())
        .command((item) => this.removeClickEvent.emit(item.id))
        .build(),
    ]);

    this.config = builder
      .dataSource({
        fetch: (): Observable<TableDataPayload<AdminBoxSaleGroupCosmetic>> => {
          const items = this.items();

          return of(new TableDataPayload(items, items.length, 1, items.length));
        },
      })
      .build();
  }

  private refreshListAfterItemsChanges(): void {
    this.table.refresh();
  }

  private initializeItemsRefreshEffect(): void {
    effect(() => {
      this.items();
      this.refreshListAfterItemsChanges();
    });
  }
}
